/**
 * Routing.
 *
 * The desktop app is loaded from a file-backed loopback server, so history
 * routing would need server rewrites for no benefit. The hash is enough and
 * works identically in the browser build.
 */

import { startTransition, useCallback, useEffect, useState } from 'react';

export type Route = { head: string; parts: string[]; query: Record<string, string> };

export function parseHash(hash = location.hash): Route {
  const raw = hash.replace(/^#\/?/, '');
  const [pathPart, queryPart] = raw.split('?');
  const parts = (pathPart ?? '').split('/').filter(Boolean);
  const query: Record<string, string> = {};
  if (queryPart) {
    for (const pair of queryPart.split('&')) {
      const [k, v] = pair.split('=');
      if (k) query[decodeURIComponent(k)] = decodeURIComponent(v ?? '');
    }
  }
  // Home is the front door: what is happening, and the two things you might
  // want to do about it. Parties is one press away from there.
  return { head: parts[0] ?? 'home', parts, query };
}

/** Rebuilds a hash from its parts. Empty values are dropped rather than
    written as `key=`, so a cleared filter leaves a clean address. */
export function buildHash(parts: string[], query: Record<string, string | undefined>): string {
  const pairs = Object.entries(query)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(String(v)));
  return '#/' + parts.join('/') + (pairs.length ? '?' + pairs.join('&') : '');
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseHash());
  useEffect(() => {
    /*
     * The new page is prepared without blocking the old one.
     *
     * A route change unmounts a whole screen and mounts another, and React
     * was doing all of it in one go - which on a page with any real list in
     * it is a frame or two of nothing while the browser is busy. Inside a
     * transition React can build the new screen across several frames and
     * keep the old one on screen and responsive until it is ready, so the
     * hand-over is one paint instead of a stall.
     *
     * The address bar still changes immediately; only the render waits.
     */
    const onChange = () => startTransition(() => setRoute(parseHash()));
    window.addEventListener('hashchange', onChange);
    /*
     * replaceState does not fire hashchange, and this module uses it for
     * filters that should not each leave a history entry. Without a matching
     * synthetic event the address would change and nothing would re-render.
     */
    window.addEventListener('pugo:hashreplace', onChange);
    return () => {
      window.removeEventListener('hashchange', onChange);
      window.removeEventListener('pugo:hashreplace', onChange);
    };
  }, []);
  return route;
}

export function go(path: string): void {
  location.hash = path.startsWith('#') ? path : '#/' + path.replace(/^\/+/, '');
}

/** Same destination, no new history entry. For filters, where Back should
    leave the screen rather than step through every choice made on it. */
export function replaceHash(hash: string): void {
  const next = hash.startsWith('#') ? hash : '#/' + hash.replace(/^\/+/, '');
  if (next === location.hash) return;
  history.replaceState(null, '', next);
  window.dispatchEvent(new Event('pugo:hashreplace'));
}

export type QueryStateOptions = {
  /** Values other than these are ignored, so a hand-edited address cannot put
      a control into a state it has no option for. */
  allow?: readonly string[];
  /** Whether changing this value adds a history entry. Discrete choices
      usually should; a text field being typed into should not. */
  push?: boolean;
};

/**
 * A filter that lives in the address bar.
 *
 * This exists because the filters on the parties screen did not. Opening
 * `#/parties?game=valorant` from the home page set the address and nothing
 * else: the select still read "All games" and all eleven parties were listed,
 * because the state behind it was a plain useState that nothing ever fed from
 * the route. The label and the results were both wrong, not just the label.
 *
 * Fixing that one control would have left the same hole behind every other
 * one, so the address is the single source of truth instead. Everything falls
 * out of that: a link someone sends opens filtered, refresh keeps the filter,
 * Back and Forward step through choices, and the control cannot disagree with
 * the results because both read the same value.
 */
export function useQueryState(
  key: string,
  fallback = '',
  options: QueryStateOptions = {}
): [string, (next: string) => void] {
  const route = useRoute();
  const { allow, push = true } = options;

  const raw = route.query[key];
  const value = raw !== undefined && (!allow || allow.includes(raw)) ? raw : fallback;

  const set = useCallback((next: string) => {
    const current = parseHash();
    const query = { ...current.query, [key]: next };
    /* The fallback is what an absent key means, so writing it out would make
       two addresses for one state and break Back in a way nobody could see. */
    if (next === fallback || next === '') delete query[key];
    const hash = buildHash(current.parts, query);
    if (push) go(hash);
    else replaceHash(hash);
  }, [key, fallback, push]);

  return [value, set];
}
